import { customBlockColors } from "@/utils/colorConfig";
import initCustomFields from "./custom-fields";

/**
 * Connect scratch blocks with the vm
 * @param {VirtualMachine} vm - The scratch vm
 * @param {Bool} useCatBlocks - Whether to use cat blocks rendering of ScratchBlocks
 * @return {ScratchBlocks} ScratchBlocks connected with the vm
 */
export default function (vm, useCatBlocks, messages) {
    const ScratchBlocks = useCatBlocks
        ? require("cat-blocks")
        : require("scratch-blocks");

    // Create intl callback for custom fields
    const intlCallback = (message) => {
        // Try to get message from messages object first
        if (messages && typeof messages === 'object') {
            // Map Chinese messages to the corresponding keys
            const messageMap = {
                "已上传": "gui.customFields.uploaded",
                "上传图片": "gui.customFields.uploadImage",
                "请选择有效的图片文件": "gui.customFields.invalidFileType",
                "图片大小不能超过 500kb\n当前大小：{size}KB\n请选择更小的图片": "gui.customFields.fileTooLarge",
                "上传失败": "gui.customFields.uploadFailed",
                "图片上传失败：{error}": "gui.customFields.uploadError",
                "图片上传失败，请重试": "gui.customFields.uploadRetry",
            };
            
            const key = messageMap[message];
            if (key && messages[key]) {
                return messages[key];
            }
        }
        // Fallback to original message
        return message;
    };

    initCustomFields(ScratchBlocks, intlCallback);

    const jsonForMenuBlock = function (name, menuOptionsFn, colors, start) {
        return {
            message0: "%1",
            args0: [
                {
                    type: "field_dropdown",
                    name: name,
                    options: function () {
                        return start.concat(menuOptionsFn());
                    },
                },
            ],
            inputsInline: true,
            output: "String",
            colour: colors.secondary,
            colourSecondary: colors.secondary,
            colourTertiary: colors.tertiary,
            colourQuaternary: colors.quaternary,
            outputShape: ScratchBlocks.OUTPUT_SHAPE_ROUND,
        };
    };

    const jsonForHatBlockMenu = function (
        hatName,
        name,
        menuOptionsFn,
        colors,
        start
    ) {
        return {
            message0: hatName,
            args0: [
                {
                    type: "field_dropdown",
                    name: name,
                    options: function () {
                        return start.concat(menuOptionsFn());
                    },
                },
            ],
            colour: colors.primary,
            colourSecondary: colors.secondary,
            colourTertiary: colors.tertiary,
            colourQuaternary: colors.quaternary,
            extensions: ["shape_hat"],
        };
    };

    const jsonForSensingMenus = function (menuOptionsFn) {
        return {
            message0: ScratchBlocks.Msg.SENSING_OF,
            args0: [
                {
                    type: "field_dropdown",
                    name: "PROPERTY",
                    options: function () {
                        return menuOptionsFn();
                    },
                },
                {
                    type: "input_value",
                    name: "OBJECT",
                },
            ],
            output: true,
            colour: ScratchBlocks.Colours.sensing.primary,
            colourSecondary: ScratchBlocks.Colours.sensing.secondary,
            colourTertiary: ScratchBlocks.Colours.sensing.tertiary,
            colourQuaternary: ScratchBlocks.Colours.sensing.quaternary,
            outputShape: ScratchBlocks.OUTPUT_SHAPE_ROUND,
        };
    };

    const soundsMenu = function () {
        let menu = [["", ""]];
        if (vm.editingTarget && vm.editingTarget.sprite.sounds.length > 0) {
            menu = vm.editingTarget.sprite.sounds.map((sound) => [
                sound.name,
                sound.name,
            ]);
        }
        menu.push([
            ScratchBlocks.ScratchMsgs.translate("SOUND_RECORD", "record..."),
            ScratchBlocks.recordSoundCallback,
        ]);
        return menu;
    };

    const costumesMenu = function () {
        if (vm.editingTarget && vm.editingTarget.getCostumes().length > 0) {
            return vm.editingTarget
                .getCostumes()
                .map((costume) => [costume.name, costume.name]);
        }
        return [["", ""]];
    };

    const backdropsMenu = function () {
        const next = ScratchBlocks.ScratchMsgs.translate(
            "LOOKS_NEXTBACKDROP",
            "next backdrop"
        );
        const previous = ScratchBlocks.ScratchMsgs.translate(
            "LOOKS_PREVIOUSBACKDROP",
            "previous backdrop"
        );
        const random = ScratchBlocks.ScratchMsgs.translate(
            "LOOKS_RANDOMBACKDROP",
            "random backdrop"
        );
        if (
            vm.runtime.targets[0] &&
            vm.runtime.targets[0].getCostumes().length > 0
        ) {
            return vm.runtime.targets[0]
                .getCostumes()
                .map((costume) => [costume.name, costume.name])
                .concat([
                    [next, "next backdrop"],
                    [previous, "previous backdrop"],
                    [random, "random backdrop"],
                ]);
        }
        return [["", ""]];
    };

    const backdropNamesMenu = function () {
        const stage = vm.runtime.getTargetForStage();
        if (stage && stage.getCostumes().length > 0) {
            return stage
                .getCostumes()
                .map((costume) => [costume.name, costume.name]);
        }
        return [["", ""]];
    };

    const spriteMenu = function () {
        const sprites = [];
        for (const targetId in vm.runtime.targets) {
            if (
                !Object.prototype.hasOwnProperty.call(
                    vm.runtime.targets,
                    targetId
                )
            )
                continue;
            if (vm.runtime.targets[targetId].isOriginal) {
                if (!vm.runtime.targets[targetId].isStage) {
                    if (vm.runtime.targets[targetId] === vm.editingTarget) {
                        continue;
                    }
                    sprites.push([
                        vm.runtime.targets[targetId].sprite.name,
                        vm.runtime.targets[targetId].sprite.name,
                    ]);
                }
            }
        }
        return sprites;
    };

    const cloneMenu = function () {
        if (vm.editingTarget && vm.editingTarget.isStage) {
            const menu = spriteMenu();
            if (menu.length === 0) {
                return [["", ""]]; // Empty menu matches Scratch 2 behavior
            }
            return menu;
        }
        const myself = ScratchBlocks.ScratchMsgs.translate(
            "CONTROL_CREATECLONEOF_MYSELF",
            "myself"
        );
        return [[myself, "_myself_"]].concat(spriteMenu());
    };

    const soundColors = ScratchBlocks.Colours.sounds;

    const looksColors = ScratchBlocks.Colours.looks;

    const sensingColors = ScratchBlocks.Colours.sensing;

    const controlColors = ScratchBlocks.Colours.control;

    const eventColors = ScratchBlocks.Colours.event;

    // new color map
    const motionColors = ScratchBlocks.Colours.motion;

    ScratchBlocks.Blocks.sound_sounds_menu.init = function () {
        const json = jsonForMenuBlock(
            "SOUND_MENU",
            soundsMenu,
            soundColors,
            []
        );
        this.jsonInit(json);
    };

    ScratchBlocks.Blocks.looks_costume.init = function () {
        const json = jsonForMenuBlock("COSTUME", costumesMenu, looksColors, []);
        this.jsonInit(json);
    };

    ScratchBlocks.Blocks.looks_backdrops.init = function () {
        const json = jsonForMenuBlock(
            "BACKDROP",
            backdropsMenu,
            looksColors,
            []
        );
        this.jsonInit(json);
    };

    ScratchBlocks.Blocks.event_whenbackdropswitchesto.init = function () {
        const json = jsonForHatBlockMenu(
            ScratchBlocks.Msg.EVENT_WHENBACKDROPSWITCHESTO,
            "BACKDROP",
            backdropNamesMenu,
            eventColors,
            []
        );
        this.jsonInit(json);
    };

    ScratchBlocks.Blocks.motion_pointtowards_menu.init = function () {
        const mouse = ScratchBlocks.ScratchMsgs.translate(
            "MOTION_POINTTOWARDS_POINTER",
            "mouse-pointer"
        );
        const json = jsonForMenuBlock("TOWARDS", spriteMenu, motionColors, [
            [mouse, "_mouse_"],
        ]);
        this.jsonInit(json);
    };

    ScratchBlocks.Blocks.motion_goto_menu.init = function () {
        const random = ScratchBlocks.ScratchMsgs.translate(
            "MOTION_GOTO_RANDOM",
            "random position"
        );
        const mouse = ScratchBlocks.ScratchMsgs.translate(
            "MOTION_GOTO_POINTER",
            "mouse-pointer"
        );
        const json = jsonForMenuBlock("TO", spriteMenu, motionColors, [
            [random, "_random_"],
            [mouse, "_mouse_"],
        ]);
        this.jsonInit(json);
    };

    ScratchBlocks.Blocks.motion_glideto_menu.init = function () {
        const random = ScratchBlocks.ScratchMsgs.translate(
            "MOTION_GLIDETO_RANDOM",
            "random position"
        );
        const mouse = ScratchBlocks.ScratchMsgs.translate(
            "MOTION_GLIDETO_POINTER",
            "mouse-pointer"
        );
        const json = jsonForMenuBlock("TO", spriteMenu, motionColors, [
            [random, "_random_"],
            [mouse, "_mouse_"],
        ]);
        this.jsonInit(json);
    };

    ScratchBlocks.Blocks.sensing_of_object_menu.init = function () {
        const stage = ScratchBlocks.ScratchMsgs.translate(
            "SENSING_OF_STAGE",
            "Stage"
        );
        const json = jsonForMenuBlock("OBJECT", spriteMenu, sensingColors, [
            [stage, "_stage_"],
        ]);
        this.jsonInit(json);
    };

    ScratchBlocks.Blocks.sensing_of.init = function () {
        const blockId = this.id;
        const blockType = this.type;

        // Get the sensing_of block from vm.
        let defaultSensingOfBlock;
        const blocks = vm.runtime.flyoutBlocks._blocks;
        Object.keys(blocks).forEach((id) => {
            const block = blocks[id];
            if (id === blockType || (block && block.opcode === blockType)) {
                defaultSensingOfBlock = block;
            }
        });

        // Function that fills in menu for the first input in the sensing block.
        // Called every time it opens since it depends on the values in the other block input.
        const menuFn = function () {
            const stageOptions = [
                [ScratchBlocks.Msg.SENSING_OF_BACKDROPNUMBER, "backdrop #"],
                [ScratchBlocks.Msg.SENSING_OF_BACKDROPNAME, "backdrop name"],
                [ScratchBlocks.Msg.SENSING_OF_VOLUME, "volume"],
            ];
            const spriteOptions = [
                [ScratchBlocks.Msg.SENSING_OF_XPOSITION, "x position"],
                [ScratchBlocks.Msg.SENSING_OF_YPOSITION, "y position"],
                [ScratchBlocks.Msg.SENSING_OF_DIRECTION, "direction"],
                [ScratchBlocks.Msg.SENSING_OF_COSTUMENUMBER, "costume #"],
                [ScratchBlocks.Msg.SENSING_OF_COSTUMENAME, "costume name"],
                [ScratchBlocks.Msg.SENSING_OF_SIZE, "size"],
                [ScratchBlocks.Msg.SENSING_OF_VOLUME, "volume"],
            ];
            if (vm.editingTarget) {
                let lookupBlocks = vm.editingTarget.blocks;
                let sensingOfBlock = lookupBlocks.getBlock(blockId);

                // The block doesn't exist, but should be in the flyout. Look there.
                if (!sensingOfBlock) {
                    sensingOfBlock =
                        vm.runtime.flyoutBlocks.getBlock(blockId) ||
                        defaultSensingOfBlock;
                    // If we still don't have a block, just return an empty list . This happens during
                    // scratch blocks construction.
                    if (!sensingOfBlock) {
                        return [["", ""]];
                    }
                    // The block was in the flyout so look up future block info there.
                    lookupBlocks = vm.runtime.flyoutBlocks;
                }
                const sort = function (options) {
                    options.sort(
                        ScratchBlocks.scratchBlocksUtils.compareStrings
                    );
                };
                // Get all the stage variables (no lists) so we can add them to menu when the stage is selected.
                const stageVariableOptions = vm.runtime
                    .getTargetForStage()
                    .getAllVariableNamesInScopeByType("");
                sort(stageVariableOptions);
                const stageVariableMenuItems = stageVariableOptions.map(
                    (variable) => [variable, variable]
                );
                if (
                    sensingOfBlock.inputs.OBJECT.shadow !==
                    sensingOfBlock.inputs.OBJECT.block
                ) {
                    // There's a block dropped on top of the menu. It'd be nice to evaluate it and
                    // return the correct list, but that is tricky. Scratch2 just returns stage options
                    // so just do that here too.
                    return stageOptions.concat(stageVariableMenuItems);
                }
                const menuBlock = lookupBlocks.getBlock(
                    sensingOfBlock.inputs.OBJECT.shadow
                );
                const selectedItem = menuBlock.fields.OBJECT.value;
                if (selectedItem === "_stage_") {
                    return stageOptions.concat(stageVariableMenuItems);
                }
                // Get all the local variables (no lists) and add them to the menu.
                const target = vm.runtime.getSpriteTargetByName(selectedItem);
                let spriteVariableOptions = [];
                // The target should exist, but there are ways for it not to (e.g. #4203).
                if (target) {
                    spriteVariableOptions =
                        target.getAllVariableNamesInScopeByType("", true);
                    sort(spriteVariableOptions);
                }
                const spriteVariableMenuItems = spriteVariableOptions.map(
                    (variable) => [variable, variable]
                );
                return spriteOptions.concat(spriteVariableMenuItems);
            }
            return [["", ""]];
        };

        const json = jsonForSensingMenus(menuFn);
        this.jsonInit(json);
    };

    ScratchBlocks.Blocks.sensing_distancetomenu.init = function () {
        const mouse = ScratchBlocks.ScratchMsgs.translate(
            "SENSING_DISTANCETO_POINTER",
            "mouse-pointer"
        );
        const json = jsonForMenuBlock(
            "DISTANCETOMENU",
            spriteMenu,
            sensingColors,
            [[mouse, "_mouse_"]]
        );
        this.jsonInit(json);
    };

    ScratchBlocks.Blocks.sensing_touchingobjectmenu.init = function () {
        const mouse = ScratchBlocks.ScratchMsgs.translate(
            "SENSING_TOUCHINGOBJECT_POINTER",
            "mouse-pointer"
        );
        const edge = ScratchBlocks.ScratchMsgs.translate(
            "SENSING_TOUCHINGOBJECT_EDGE",
            "edge"
        );
        const json = jsonForMenuBlock(
            "TOUCHINGOBJECTMENU",
            spriteMenu,
            sensingColors,
            [
                [mouse, "_mouse_"],
                [edge, "_edge_"],
            ]
        );
        this.jsonInit(json);
    };

    ScratchBlocks.Blocks.control_create_clone_of_menu.init = function () {
        const json = jsonForMenuBlock(
            "CLONE_OPTION",
            cloneMenu,
            controlColors,
            []
        );
        this.jsonInit(json);
    };

    ScratchBlocks.VerticalFlyout.getCheckboxState = function (blockId) {
        const monitoredBlock = vm.runtime.monitorBlocks._blocks[blockId];
        return monitoredBlock ? monitoredBlock.isMonitored : false;
    };

    ScratchBlocks.FlyoutExtensionCategoryHeader.getExtensionState = function (
        extensionId
    ) {
        if (vm.getPeripheralIsConnected(extensionId)) {
            return ScratchBlocks.StatusButtonState.READY;
        }
        return ScratchBlocks.StatusButtonState.NOT_READY;
    };

    ScratchBlocks.FieldNote.playNote_ = function (noteNum, extensionId) {
        vm.runtime.emit("PLAY_NOTE", noteNum, extensionId);
    };

    // Use a collator's compare instead of localeCompare which internally
    // creates a collator. Using this is a lot faster in browsers that create a
    // collator for every localeCompare call.
    const collator = new Intl.Collator([], {
        sensitivity: "base",
        numeric: true,
    });
    ScratchBlocks.scratchBlocksUtils.compareStrings = function (str1, str2) {
        return collator.compare(str1, str2);
    };

    // Blocks wants to know if 3D CSS transforms are supported. The cross
    // section of browsers Scratch supports and browsers that support 3D CSS
    // transforms will make the return always true.
    //
    // Shortcutting to true lets us skip an expensive style recalculation when
    // first loading the Scratch editor.
    ScratchBlocks.utils.is3dSupported = function () {
        return true;
    };

    ScratchBlocks.Blocks.motion_dance = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.dance"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };

    ScratchBlocks.Blocks.motion_forward = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_forward"],
                args0: [
                    {
                        type: "input_value",
                        name: "STEPS",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };

    // ScratchBlocks.Blocks.motion_moveleft = {
    //     init: function () {
    //         this.jsonInit({
    //             message0: messages["gui.opcodeLabels.motion_moveleft"], // Added %1 for the image
    //             args0: [
    //                 {
    //                     type: "field_image",
    //                     src: "static/extension_icons/moveLeft.svg", // Placeholder image
    //                     width: 17, // Adjust as needed
    //                     height: 17, // Adjust as needed
    //                     alt: "Left Arrow", // Alt text for accessibility
    //                     flip_rtl: true, // Flip for RTL languages if needed
    //                 },
    //                 {
    //                     type: "input_value",
    //                     name: "STEPS",
    //                 },
    //             ],
    //             previousStatement: null,
    //             nextStatement: null,
    //             colour: motionColors.primary,
    //             colourSecondary: motionColors.secondary,
    //             colourTertiary: motionColors.tertiary,
    //         });
    //     },
    // };

    // ScratchBlocks.Blocks.motion_moveright = {
    //     init: function () {
    //         this.jsonInit({
    //             message0: messages["gui.opcodeLabels.motion_moveright"], // Added %1 for the image
    //             args0: [
    //                 {
    //                     type: "field_image",
    //                     src: "static/extension_icons/moveRight.svg", // Placeholder image
    //                     width: 17, // Adjust as needed
    //                     height: 17, // Adjust as needed
    //                     alt: "Right Arrow", // Alt text for accessibility
    //                     flip_rtl: true, // Flip for RTL languages if needed
    //                 },
    //                 {
    //                     type: "input_value",
    //                     name: "STEPS",
    //                 },
    //             ],
    //             previousStatement: null,
    //             nextStatement: null,
    //             colour: motionColors.primary,
    //             colourSecondary: motionColors.secondary,
    //             colourTertiary: motionColors.tertiary,
    //         });
    //     },
    // };

    ScratchBlocks.Blocks.motion_goback = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_goback"],
                args0: [
                    {
                        type: "input_value",
                        name: "steps",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };

    ScratchBlocks.Blocks.motion_gotoxyz = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_gotoxyz"],
                args0: [
                    {
                        type: "input_value",
                        name: "X",
                    },
                    {
                        type: "input_value",
                        name: "Y",
                    },
                    {
                        type: "input_value",
                        name: "Z",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };

    // 机器狗新增积木-start
    ScratchBlocks.Blocks.control_servo_reset = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.control_servo_reset"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };
    ScratchBlocks.Blocks.motion_nod = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_nod"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };
    ScratchBlocks.Blocks.motion_sway_x = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_sway_x"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };
    ScratchBlocks.Blocks.motion_sway_y = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_sway_y"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };
    ScratchBlocks.Blocks.motion_fall = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_fall"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };
    ScratchBlocks.Blocks.motion_retract_leg = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_retract_leg"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };
    ScratchBlocks.Blocks.motion_handshake = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_handshake"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };
    ScratchBlocks.Blocks.motion_stretch = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_stretch"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };
    ScratchBlocks.Blocks.motion_jump = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_jump"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };
    ScratchBlocks.Blocks.self_dog_move_control = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.self_dog_move_control"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };
    // 机器狗新增积木-end

    ScratchBlocks.Blocks.motion_stop = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_stop"],
                args0: [
                    {
                        type: "input_value",
                        name: "seconds",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };

    ScratchBlocks.Blocks.motion_speed = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_speed"],
                args0: [
                    {
                        type: "input_value",
                        name: "speed",
                        check: "Number",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
        /**
         * 验证并自动修正输入值
         * - >10: 自动改为10
         * - <1: 自动改为1
         * - 非数字: 自动改为5(默认值)
         */
        onchange: function (event) {
            if (!this.workspace || this.isInFlyout) return;
            
            const speedInput = this.getInputTargetBlock('speed');
            if (speedInput) {
                // 如果是数字积木,检查并修正值
                const field = speedInput.getField('NUM');
                if (field) {
                    const value = parseInt(field.getValue());
                    
                    // 非数字(NaN): 设置为默认值5
                    if (isNaN(value)) {
                        field.setValue(5);
                        return;
                    }
                    
                    // 大于10: 自动改为10
                    if (value > 10) {
                        field.setValue(10);
                        return;
                    }
                    
                    // 小于1: 自动改为1
                    if (value < 1) {
                        field.setValue(1);
                        return;
                    }
                }
            }
        },
    };

    // 新增积木-2026.04.18-start
    // 前进
    ScratchBlocks.Blocks.motion_advance = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_advance"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };

    // 后退
    ScratchBlocks.Blocks.motion_backward = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_backward"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };

    // 左转
    ScratchBlocks.Blocks.motion_turn_left = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_turn_left"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };

    // 右转
    ScratchBlocks.Blocks.motion_turn_right = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_turn_right"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };

    // 停止
    ScratchBlocks.Blocks.motion_stop_move = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_stop_move"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };

    // 开始巡线
    ScratchBlocks.Blocks.motion_start_line_tracking = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_start_line_tracking"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };

    // 停止巡线
    ScratchBlocks.Blocks.motion_stop_line_tracking = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.motion_stop_line_tracking"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: motionColors.primary,
                colourSecondary: motionColors.secondary,
                colourTertiary: motionColors.tertiary,
            });
        },
    };
    // 新增积木-2026.04.18-end

    // 设置小车灯光颜色
    ScratchBlocks.Blocks.looks_set_rgb = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.looks_set_rgb"],
                args0: [
                    {
                        type: "field_dropdown",
                        name: "index",
                        options: [
                            ["0", "0"],
                            ["1", "1"],
                            ["2", "2"],
                            ["3", "3"],
                            ["4", "4"],
                            ["5", "5"],
                        ],
                    },
                    {
                        type: "input_value",
                        name: "color",
                        check: "Colour",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.display.primary,
                colourSecondary: customBlockColors.display.secondary,
                colourTertiary: customBlockColors.display.tertiary,
            });
        },
    };

    // 打开小车所有灯光
    ScratchBlocks.Blocks.looks_turn_on_all_lights = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.looks_turn_on_all_lights"],
                args0: [
                    {
                        type: "input_value",
                        name: "color",
                        check: "Colour",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.display.primary,
                colourSecondary: customBlockColors.display.secondary,
                colourTertiary: customBlockColors.display.tertiary,
            });
        },
    };

    // 关闭小车所有灯光
    ScratchBlocks.Blocks.looks_turn_off_all_lights = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.looks_turn_off_all_lights"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.display.primary,
                colourSecondary: customBlockColors.display.secondary,
                colourTertiary: customBlockColors.display.tertiary,
            });
        },
    };

    // 显示文本时长
    // ScratchBlocks.Blocks.looks_show_text_duration = {
    //     init: function () {
    //         this.jsonInit({
    //             message0: messages["gui.opcodeLabels.looks_show_text_duration"],
    //             args0: [
    //                 {
    //                     type: "input_value",
    //                     name: "text",
    //                     check: "String",
    //                 },
    //                 {
    //                     type: "input_value",
    //                     name: "duration",
    //                     check: "Number",
    //                 },
    //             ],
    //             previousStatement: null,
    //             nextStatement: null,
    //             colour: customBlockColors.display.primary,
    //             colourSecondary: customBlockColors.display.secondary,
    //             colourTertiary: customBlockColors.display.tertiary,
    //         });
    //     },
    // };

    ScratchBlocks.Blocks.sounds_play = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.sounds_play"],
                args0: [
                    {
                        type: "input_value",
                        name: "text",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.voice.primary,
                colourSecondary: customBlockColors.voice.secondary,
                colourTertiary: customBlockColors.voice.tertiary,
            });
        },
    };

    ScratchBlocks.Blocks.looks_show_expression = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.looks_show_expression"],
                args0: [
                    {
                        type: "field_dropdown",
                        name: "expression",
                        options: function () {
                            return [
                                ["😂", "0"],
                                ["😆", "1"],
                                ["🤔", "2"],
                                ["😱", "3"],
                                ["😠", "4"],
                                ["🥰", "5"],
                                ["😎", "6"],
                                ["😮", "7"],
                            ];
                        },
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.display.primary,
                colourSecondary: customBlockColors.display.secondary,
                colourTertiary: customBlockColors.display.tertiary,
            });
        },
    };

    ScratchBlocks.Blocks.looks_show_text = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.looks_show_text"],
                args0: [
                    {
                        type: "input_value",
                        name: "show_text",
                    },
                    {
                        type: "input_value",
                        name: "second",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.display.primary,
                colourSecondary: customBlockColors.display.secondary,
                colourTertiary: customBlockColors.display.tertiary,
            });
        },
    };

    ScratchBlocks.Blocks.looks_screen_clear = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.looks_screen_clear"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.display.primary,
                colourSecondary: customBlockColors.display.secondary,
                colourTertiary: customBlockColors.display.tertiary,
            });
        },
    };

    ScratchBlocks.Blocks.looks_show_image = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.looks_show_image"],
                args0: [
                    {
                        type: "field_image_upload",
                        name: "IMAGE_DATA",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.display.primary,
                colourSecondary: customBlockColors.display.secondary,
                colourTertiary: customBlockColors.display.tertiary,
            });
        },
    };

    // ScratchBlocks.Blocks.image_shoot = {
    //     init: function () {
    //         this.jsonInit({
    //             message0: "拍照",
    //             args0: [],
    //             previousStatement: null,
    //             nextStatement: null,
    //             colour: customBlockColors.image.primary,
    //             colourSecondary: customBlockColors.image.secondary,
    //             colourTertiary: customBlockColors.image.tertiary,
    //         });
    //     },
    // };

    // ScratchBlocks.Blocks.image_identify_color = {
    //     init: function () {
    //         this.jsonInit({
    //             message0: "识别颜色",
    //             args0: [],
    //             previousStatement: null,
    //             nextStatement: null,
    //             colour: customBlockColors.image.primary,
    //             colourSecondary: customBlockColors.image.secondary,
    //             colourTertiary: customBlockColors.image.tertiary,
    //         });
    //     },
    // };

    ScratchBlocks.Blocks.image_identify_object = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.image_identify_object"],
                message1: "%1",
                message2: messages["gui.opcodeLabels.image_identify_object_else"],
                message3: "%1",
                args0: [
                    {
                        type: "field_dropdown",
                        name: "expression",
                        options: function () {
                            return [
                                [messages["gui.opcodeLabels.shape"], "形状"],
                                [messages["gui.opcodeLabels.object"], "物体"],
                                [messages["gui.opcodeLabels.color"], "颜色"],
                                [messages["gui.opcodeLabels.gesture"], "手势"],
                            ];
                        },
                        value: "形状",
                    },
                    {
                        type: "input_value",
                        name: "text",
                    },
                ],
                args1: [
                    {
                        type: "input_statement",
                        name: "substack",
                    },
                ],
                args2: [],
                args3: [
                    {
                        type: "input_statement",
                        name: "substack2",
                    },
                ],
                inputsInline: false,
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.image.primary,
                colourSecondary: customBlockColors.image.secondary,
                colourTertiary: customBlockColors.image.tertiary,
            });
        },
    };

    // ScratchBlocks.Blocks.image_show = {
    //     init: function () {
    //         this.jsonInit({
    //             message0: "显示图像",
    //             args0: [],
    //             previousStatement: null,
    //             nextStatement: null,
    //             colour: customBlockColors.image.primary,
    //             colourSecondary: customBlockColors.image.secondary,
    //             colourTertiary: customBlockColors.image.tertiary,
    //         });
    //     },
    // };

    ScratchBlocks.Blocks.event_delay = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_delay"],
                args0: [
                    {
                        type: "input_value",
                        name: "second",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.event_custom.primary,
                colourSecondary: customBlockColors.event_custom.secondary,
                colourTertiary: customBlockColors.event_custom.tertiary,
            });
        },
    };

    ScratchBlocks.Blocks.event_repeat = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_repeat"],
                message1: "%1",
                args0: [
                    {
                        type: "input_value",
                        name: "times",
                    },
                ],
                args1: [
                    {
                        type: "input_statement",
                        name: "substack",
                    },
                ],
                args2: [],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.event_custom.primary,
                colourSecondary: customBlockColors.event_custom.secondary,
                colourTertiary: customBlockColors.event_custom.tertiary,
            });
        },
    };

    ScratchBlocks.Blocks.event_gyro = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_gyro"],
                message1: "%1",
                args0: [
                    {
                        type: "field_dropdown",
                        name: "express",
                        options: function () {
                            return [
                                [messages["gui.opcodeLabels.greater"], "greater"],
                                [messages["gui.opcodeLabels.less"], "less"],
                                [messages["gui.opcodeLabels.equal"], "equal"],
                            ];
                        },
                    },
                    {
                        type: "input_value",
                        name: "angle",
                    },
                ],
                args1: [
                    {
                        type: "input_statement",
                        name: "substack",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.event_custom.primary,
                colourSecondary: customBlockColors.event_custom.secondary,
                colourTertiary: customBlockColors.event_custom.tertiary,
            });
        },
    };

    ScratchBlocks.Blocks.event_detect_cliff = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_detect_cliff"],
                message1: "%1",
                args1: [
                    {
                        type: "input_statement",
                        name: "substack",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.event_custom.primary,
                colourSecondary: customBlockColors.event_custom.secondary,
                colourTertiary: customBlockColors.event_custom.tertiary,
            });
        },
    };

    ScratchBlocks.Blocks.event_back_detect_cliff = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_back_detect_cliff"],
                message1: "%1",
                args1: [
                    {
                        type: "input_statement",
                        name: "substack",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.event_custom.primary,
                colourSecondary: customBlockColors.event_custom.secondary,
                colourTertiary: customBlockColors.event_custom.tertiary,
            });
        },
    };

    // 当按下按键 - 帽子积木(带if卡槽)
    ScratchBlocks.Blocks.event_button_pressed = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_button_pressed"],
                message1: "%1",
                args0: [
                    {
                        type: "field_dropdown",
                        name: "button",
                        options: function () {
                            return [
                                [messages["gui.opcodeLabels.left_button"], "left"],
                                [messages["gui.opcodeLabels.right_button"], "right"],
                            ];
                        },
                    },
                ],
                args1: [
                    {
                        type: "input_statement",
                        name: "substack",
                    },
                ],
                colour: customBlockColors.event_custom.primary,
                colourSecondary: customBlockColors.event_custom.secondary,
                colourTertiary: customBlockColors.event_custom.tertiary,
            });
        },
    };

    // 当按键按下执行 - 帽子积木(类似event_gyro结构)
    ScratchBlocks.Blocks.event_if_button = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_if_button"],
                message1: "%1",
                message2: messages["gui.opcodeLabels.else_label"],
                args0: [
                    {
                        type: "field_dropdown",
                        name: "button",
                        options: function () {
                            return [
                                [messages["gui.opcodeLabels.left_button"], "left"],
                                [messages["gui.opcodeLabels.right_button"], "right"],
                            ];
                        },
                    },
                ],
                args1: [
                    {
                        type: "input_statement",
                        name: "substack",
                    },
                ],
                message2: messages["gui.opcodeLabels.else_label"],
                message3: "%1",
                args3: [
                    {
                        type: "input_statement",
                        name: "substack2",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.event_custom.primary,
                colourSecondary: customBlockColors.event_custom.secondary,
                colourTertiary: customBlockColors.event_custom.tertiary,
            });
        },
    };

    // 如果-那么-否则(判断积木)
    ScratchBlocks.Blocks.event_if_else = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_if_else"],
                message1: "%1",
                message2: messages["gui.opcodeLabels.else_label"],
                message3: "%1",
                args0: [
                    {
                        type: "input_value",
                        name: "CONDITION",
                    },
                ],
                args1: [
                    {
                        type: "input_statement",
                        name: "substack",
                    },
                ],
                args2: [],
                args3: [
                    {
                        type: "input_statement",
                        name: "substack2",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.event_custom.primary,
                colourSecondary: customBlockColors.event_custom.secondary,
                colourTertiary: customBlockColors.event_custom.tertiary,
            });
        },
    };

    // 重复满足/不满足条件(判断循环积木)
    ScratchBlocks.Blocks.event_repeat_condition = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_repeat_condition"],
                message1: "%1",
                // message2: messages["gui.opcodeLabels.else_label"],
                args0: [
                    {
                        type: "field_dropdown",
                        name: "condition_type",
                        options: function () {
                            return [
                                [messages["gui.opcodeLabels.satisfy"], "satisfy"],
                                [messages["gui.opcodeLabels.unsatisfy"], "unsatisfy"],
                            ];
                        },
                    },
                    {
                        type: "input_value",
                        name: "CONDITION",
                    },
                ],
                args1: [
                    {
                        type: "input_statement",
                        name: "substack",
                    },
                ],
                args2: [],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.event_custom.primary,
                colourSecondary: customBlockColors.event_custom.secondary,
                colourTertiary: customBlockColors.event_custom.tertiary,
            });
        },
    };

    // 跳出循环
    // ScratchBlocks.Blocks.event_break_loop = {
    //     init: function () {
    //         this.jsonInit({
    //             message0: messages["gui.opcodeLabels.event_break_loop"],
    //             previousStatement: null,
    //             nextStatement: null,
    //             colour: customBlockColors.event_custom.primary,
    //             colourSecondary: customBlockColors.event_custom.secondary,
    //             colourTertiary: customBlockColors.event_custom.tertiary,
    //         });
    //     },
    // };

    // 陀螺仪清零
    ScratchBlocks.Blocks.event_gyro_reset = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_gyro_reset"],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.event_custom.primary,
                colourSecondary: customBlockColors.event_custom.secondary,
                colourTertiary: customBlockColors.event_custom.tertiary,
            });
        },
    };

    // 且/或逻辑运算 - 圆形积木
    ScratchBlocks.Blocks.event_and_or = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_and_or"],
                args0: [
                    {
                        type: "input_value",
                        name: "VALUE1",
                        check: "Number",
                    },
                    {
                        type: "field_dropdown",
                        name: "OP",
                        options: function () {
                            return [
                                [messages["gui.opcodeLabels.and"], "AND"],
                                [messages["gui.opcodeLabels.or"], "OR"],
                            ];
                        },
                    },
                    {
                        type: "input_value",
                        name: "VALUE2",
                        check: "Number",
                    },
                ],
                output: "Boolean",
                outputShape: ScratchBlocks.OUTPUT_SHAPE_ROUND,
                colour: customBlockColors.event_custom.primary,
                colourSecondary: customBlockColors.event_custom.secondary,
                colourTertiary: customBlockColors.event_custom.tertiary,
                inputsInline: true,
            });
        },
    };

    // 左循迹传感器的值 - 圆角矩形积木
    ScratchBlocks.Blocks.event_left_line_sensor = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_left_line_sensor"],
                output: null,
                outputShape: ScratchBlocks.OUTPUT_SHAPE_ROUND,
                colour: customBlockColors.event_custom.primary,
                colourSecondary: customBlockColors.event_custom.secondary,
                colourTertiary: customBlockColors.event_custom.tertiary,
            });
        },
    };

    // 右循迹传感器的值 - 圆角矩形积木
    ScratchBlocks.Blocks.event_right_line_sensor = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_right_line_sensor"],
                output: null,
                outputShape: ScratchBlocks.OUTPUT_SHAPE_ROUND,
                colour: customBlockColors.event_custom.primary,
                colourSecondary: customBlockColors.event_custom.secondary,
                colourTertiary: customBlockColors.event_custom.tertiary,
            });
        },
    };

    // 陀螺仪的值 - 圆角矩形积木
    ScratchBlocks.Blocks.event_gyro_value = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_gyro_value"],
                output: null,
                outputShape: ScratchBlocks.OUTPUT_SHAPE_ROUND,
                colour: customBlockColors.event_custom.primary,
                colourSecondary: customBlockColors.event_custom.secondary,
                colourTertiary: customBlockColors.event_custom.tertiary,
            });
        },
    };

    // 比较运算符 - 圆角矩形积木
    ScratchBlocks.Blocks.event_compare = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_compare"],
                args0: [
                    {
                        type: "input_value",
                        name: "VALUE1",
                        check: "Number",
                    },
                    {
                        type: "field_dropdown",
                        name: "OP",
                        options: function () {
                            return [
                                [messages["gui.opcodeLabels.greater_than"], ">"],
                                [messages["gui.opcodeLabels.equal_to"], "="],
                                [messages["gui.opcodeLabels.less_than"], "<"],
                            ];
                        },
                    },
                    {
                        type: "input_value",
                        name: "VALUE2",
                        check: "Number",
                    },
                ],
                output: null,
                outputShape: ScratchBlocks.OUTPUT_SHAPE_ROUND,
                colour: customBlockColors.event_custom.primary,
                colourSecondary: customBlockColors.event_custom.secondary,
                colourTertiary: customBlockColors.event_custom.tertiary,
            });
        },
    };

    

    ScratchBlocks.Blocks.event_interval = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.event_interval"],
                message1: "%1",
                message2: messages["gui.opcodeLabels.else_label"],
                args0: [
                    {
                        type: "input_value",
                        name: "second",
                    },
                    {
                        type: "input_value",
                        name: "total_times",
                    },
                ],
                args1: [
                    {
                        type: "input_statement",
                        name: "substack",
                    },
                ],
                args2: [],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.timing.primary,
                colourSecondary: customBlockColors.timing.secondary,
                colourTertiary: customBlockColors.timing.tertiary,
            });
        },
    };

    ScratchBlocks.Blocks.sys_set_volume = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.sys_set_volume"],
                args0: [
                    {
                        type: "input_value",
                        name: "volume",
                        check: "Number",
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.system.primary,
                colourSecondary: customBlockColors.system.secondary,
                colourTertiary: customBlockColors.system.tertiary,
            });
        },
        /**
         * 验证并自动修正音量值
         * - >100: 自动改为100
         * - <0: 自动改为0
         * - 非数字: 自动改为80(默认值)
         */
        onchange: function (event) {
            if (!this.workspace || this.isInFlyout) return;
            
            const volumeInput = this.getInputTargetBlock('volume');
            if (volumeInput) {
                // 如果是数字积木,检查并修正值
                const field = volumeInput.getField('NUM');
                if (field) {
                    const value = parseInt(field.getValue());
                    
                    // 非数字(NaN): 设置为默认值80
                    if (isNaN(value)) {
                        field.setValue(80);
                        return;
                    }
                    
                    // 大于100: 自动改为100
                    if (value > 100) {
                        field.setValue(100);
                        return;
                    }
                    
                    // 小于0: 自动改为0
                    if (value < 0) {
                        field.setValue(0);
                        return;
                    }
                }
            }
        },
    };

    ScratchBlocks.Blocks.sys_reboot = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.sys_reboot"],
                args0: [],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.system.primary,
                colourSecondary: customBlockColors.system.secondary,
                colourTertiary: customBlockColors.system.tertiary,
            });
        },
    };

    ScratchBlocks.Blocks.looks_theme = {
        init: function () {
            this.jsonInit({
                message0: messages["gui.opcodeLabels.looks_theme"],
                args0: [
                    {
                        type: "field_dropdown",
                        name: "theme",
                        options: function () {
                            return [
                                [messages["gui.opcodeLabels.dark"], "dark"],
                                [messages["gui.opcodeLabels.light"], "light"],
                            ];
                        },
                    },
                ],
                previousStatement: null,
                nextStatement: null,
                colour: customBlockColors.system.primary,
                colourSecondary: customBlockColors.system.secondary,
                colourTertiary: customBlockColors.system.tertiary,
            });
        },
    };

    // Register custom blocks with character limits
    // Text block with 30 character limit
    ScratchBlocks.Blocks["text_limited_30"] = {
        init: function () {
            this.jsonInit({
                message0: "%1",
                args0: [
                    {
                        type: "field_input",
                        name: "TEXT",
                        text: messages["gui.opcodeLabels.text_default"],
                    },
                ],
                output: "String",
                outputShape: ScratchBlocks.OUTPUT_SHAPE_ROUND,
                colour: ScratchBlocks.Colours.textField,
                colourSecondary: ScratchBlocks.Colours.textField,
                colourTertiary: ScratchBlocks.Colours.textField,
                colourQuaternary: ScratchBlocks.Colours.textField,
            });

            const maxLength = 50;
            const field = this.getField("TEXT");
            if (field) {
                field.setValidator(function (newValue) {
                    return typeof newValue === "string"
                        ? newValue.slice(0, maxLength)
                        : newValue;
                });

                const originalShowEditor = field.showEditor_;
                field.showEditor_ = function () {
                    originalShowEditor.call(this);
                    const htmlInput = ScratchBlocks.FieldTextInput.htmlInput_;
                    if (htmlInput) {
                        htmlInput.setAttribute("maxlength", maxLength);
                    }
                    const originalOnChange = field.onHtmlInputChange_;
                    field.onHtmlInputChange_ = function (e) {
                        if (e.target.value.length > maxLength) {
                            e.target.value = e.target.value.slice(0, maxLength);
                        }
                        originalOnChange.call(field, e);
                    };
                };
            }
        },
    };

    // Number block with angle limits (1-30 degrees)
    ScratchBlocks.Blocks["math_angle_1_30"] = {
        init: function () {
            const [min, max] = [1, 30];

            this.jsonInit({
                message0: "%1",
                args0: [
                    {
                        type: "field_number",
                        name: "NUM",
                        value: 15,
                        min: min,
                        max: max,
                        precision: 1,
                    },
                ],
                output: "Number",
                outputShape: ScratchBlocks.OUTPUT_SHAPE_ROUND,
                colour: ScratchBlocks.Colours.textField,
                colourSecondary: ScratchBlocks.Colours.textField,
                colourTertiary: ScratchBlocks.Colours.textField,
                colourQuaternary: ScratchBlocks.Colours.textField,
            });

            const field = this.getField("NUM");
            window.field = field;
            if (field) {
                field.setValidator(function (newValue) {
                    if (!newValue) return newValue;
                    const num = parseInt(newValue);
                    return isNaN(num)
                        ? newValue
                        : String(Math.max(min, Math.min(max, num)));
                });
                const originalShowEditor = field.showEditor_;
                field.showEditor_ = function () {
                    originalShowEditor.call(this);
                    const htmlInput = ScratchBlocks.FieldTextInput.htmlInput_;
                    if (htmlInput) {
                        htmlInput.setAttribute("type", "number");
                        htmlInput.setAttribute("min", min);
                        htmlInput.setAttribute("max", max);
                    }
                    const originalOnChange = field.onHtmlInputChange_;
                    field.onHtmlInputChange_ = function (e) {
                        if (e.target.value) {
                            const num = parseInt(e.target.value);
                            if (!isNaN(num) && (num < min || num > max)) {
                                e.target.value = String(
                                    Math.max(min, Math.min(max, num))
                                );
                            }
                        }
                        originalOnChange.call(field, e);
                    };
                };
            }
        },
    };

    return ScratchBlocks;
}
